import React, { useState, useEffect, useMemo, useRef } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment/moment";
import Select from "react-select";
import { RiRefreshLine } from "react-icons/ri";
import { FaFileCircleExclamation } from "react-icons/fa6";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import LeaseApiService from "../../services/apiService";

// Dropdown name used for the Status filter (change here if the backend name differs)
const AGENT_TYPES = [
    { value: "All", label: "All" },
    { value: "Internal", label: "Internal" },
    { value: "External", label: "External" },
];
const STATUS_DROPDOWN = "LRPGetStatusForAgentCommission";

const fmtMoney = (v, dp = 3) => (v === null || v === undefined || v === "" ? "" :
    Number(v).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp }));
const fmtDate = (v) => (v && moment(v).isValid() ? moment(v).format("DD-MMM-YYYY") : (v || ""));
const fmtDateTime = (v) => (v && moment(v).isValid() ? moment(v).format("DD-MMM-YYYY HH:mm") : "");

const COLS = [
    { header: "Agent Type", field: "agentType", width: 130 },
    { header: "Agent", field: "brokerName", width: 220 },
    { header: "Property", field: "propertyName", width: 200 },
    { header: "Unit No", field: "unitNo", width: 110 },
    { header: "Lease No", field: "leaseNo", width: 130 },
    { header: "Lease Date", field: "leaseDate", width: 130, date: true },
    { header: "Lease Expiry", field: "leaseExpiry", width: 130, date: true },
    { header: "Lease Status", field: "leaseStatus", width: 130 },
    { header: "Monthly Rent", field: "monthlyRent", width: 140, num: true, money: true, total: true },
    { header: "Fixed Comm %", field: "fixedCommissionPercentage", width: 130, num: true, money: true, pct: true },
    { header: "Pending Commission", field: "pendingCommissionAmount", width: 170, num: true, money: true, total: true },
    { header: "Invoice No", field: "invoiceNo", width: 140 },
    { header: "Invoice %", field: "invoicePercentage", width: 110, num: true, money: true, pct: true },
    { header: "Invoice Amount", field: "invoiceAmount", width: 150, num: true, money: true, total: true },
    { header: "Paid Amount", field: "paidAmount", width: 140, num: true, money: true, total: true },
    { header: "Balance Amount", field: "balanceAmount", width: 150, num: true, money: true, total: true },
    { header: "Payment Vouchers", field: "paymentVoucherNos", width: 200 },
    { header: "Payments", field: "paymentCount", width: 110, num: true },
    { header: "Last Paid By", field: "lastPaidBy", width: 150 },
    { header: "Last Paid On", field: "lastPaidOn", width: 160, dateTime: true },
    { header: "Commission Status", field: "commissionStatus", width: 170 },
];

const cellText = (row, col) => {
    if (col.text) return col.text(row);
    const v = row[col.field];
    if (col.money) return fmtMoney(v, col.pct ? 2 : 3);
    if (col.num) return v === null || v === undefined ? "" : Number(v).toLocaleString("en-US");
    if (col.date) return fmtDate(v);
    if (col.dateTime) return fmtDateTime(v);
    return v ?? "";
};

const toColDefs = (cols) => cols.map((c) => ({
    headerName: c.header,
    field: c.field,
    width: c.width,
    filter: true,
    floatingFilter: true,
    suppressMovable: true,
    ...(c.num ? { type: "rightAligned", filter: "agNumberColumnFilter" } : {}),
    valueFormatter: (p) => cellText(p.data || {}, c),
}));

const stripe = (p) => ({ background: p.node.rowIndex % 2 === 0 ? "#F0EDF5" : "#fff" });

const exportExcel = (rows, cols, title, file) => {
    const data = rows.map((r) => cols.map((c) => {
        if (!(c.money || c.num)) return cellText(r, c);
        const v = r[c.field];
        return v === null || v === undefined || v === "" ? "" : Number(v);
    }));
    const hasTotals = cols.some((c) => c.total);
    const totals = hasTotals
        ? cols.map((c, i) => (c.total ? rows.reduce((sum, r) => sum + (Number(r[c.field]) || 0), 0) : i === 0 ? "Total" : ""))
        : null;
    const ws = XLSX.utils.aoa_to_sheet([[title.toUpperCase()], cols.map((c) => c.header), ...data, ...(totals ? [totals] : [])]);
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: cols.length - 1 } }];
    // Auto-fit: widest of header, formatted cell values and the totals row
    ws["!cols"] = cols.map((c, ci) => {
        let max = c.header.length;
        rows.forEach((r) => { max = Math.max(max, String(cellText(r, c)).length); });
        if (totals && c.total) max = Math.max(max, fmtMoney(totals[ci]).length);
        return { wch: Math.min(Math.max(max + 3, 10), 60) };
    });
    if (ws["A1"]) ws["A1"].s = { font: { bold: true, sz: 16, color: { rgb: "FFFFFFFF" } }, fill: { fgColor: { rgb: "FF6E5199" } } };
    cols.forEach((c, ci) => {
        const h = ws[XLSX.utils.encode_cell({ r: 1, c: ci })];
        if (h) h.s = { font: { bold: true, color: { rgb: "FFFFFFFF" } }, fill: { fgColor: { rgb: "FF666666" } }, alignment: { horizontal: "center" } };
        // Number formats: amounts 3 decimals, percentages 2, counts as integers
        const fmt = c.money ? (c.pct ? "#,##0.00" : "#,##0.000") : c.num ? "#,##0" : null;
        const lastRow = 2 + rows.length + (totals ? 1 : 0);
        for (let r = 2; r < lastRow; r++) {
            const cell = ws[XLSX.utils.encode_cell({ r, c: ci })];
            if (cell && fmt && cell.t === "n") cell.z = fmt;
            if (cell && totals && r === lastRow - 1) cell.s = { font: { bold: true }, fill: { fgColor: { rgb: "FFE5E0EE" } } };
        }
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, file + ".xlsx");
};

const exportPDF = (rows, cols, title, file) => {
    const doc = new jsPDF("landscape", "pt", "A4");
    doc.setFillColor(110, 81, 153);
    doc.rect(0, 0, doc.internal.pageSize.width, 50, "F");
    doc.setTextColor(255);
    doc.setFontSize(16);
    doc.text(title, 40, 32);
    doc.setTextColor(0);
    doc.autoTable({
        startY: 60,
        head: [cols.map((c) => c.header)],
        body: rows.map((r) => cols.map((c) => cellText(r, c))),
        styles: { fontSize: 7, cellPadding: 4 },
        headStyles: { fillColor: [110, 81, 153], textColor: 255 },
        columnStyles: Object.fromEntries(cols.map((c, i) => [i, { halign: c.num ? "right" : "left" }])),
        alternateRowStyles: { fillColor: [240, 237, 245] },
    });
    doc.save(file + ".pdf");
};

const RowCount = ({ shown, total }) => (
    <div className="mt-2 small fw-bold">
        Total Rows: {shown === total ? total : `${shown} of ${total}`}
    </div>
);

const ExportButtons = ({ rows, cols, title, file }) => (
    <>
        <button className="btn btn-primary" disabled={!rows.length} onClick={() => exportPDF(rows, cols, title, file)}>Export as PDF</button>
        <button className="btn btn-secondary mx-3" disabled={!rows.length} onClick={() => exportExcel(rows, cols, title, file)}>Export as Excel</button>
    </>
);

const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? "#F5F5F5" : null,
        color: "#000",
        ":hover": { backgroundColor: "#F5F5F5", color: "#000" },
    }),
};

const DEFAULT_FILTERS = () => ({
    agentType: AGENT_TYPES[0],
    property: null,
    broker: null,
    status: null,
});

// A 204 means "no rows", not an error.
const unwrap = (res) => {
    const body = res?.data;
    if (!body || body.statusCode === 204) return [];
    if (body.statusCode && body.statusCode >= 400) throw new Error(body.message || "Request failed");
    const list = body.result ?? body.data;
    return Array.isArray(list) ? list : [];
};

const AgentCommissionSummary = () => {
    const apiService = useRef(new LeaseApiService()).current;
    const reqId = useRef(0);
    const [FilterTab, setFilterTab] = useState(true);
    const [agentList, setAgentList] = useState([]);
    const [propertyList, setPropertyList] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [formData, setFormData] = useState(DEFAULT_FILTERS());
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const agentParams = new URLSearchParams();
                agentParams.append("dropdownTypes", "AgentCommissionAgentNames");
                const reportParams = new URLSearchParams();
                reportParams.append("dropdownTypes", "LRAgreeSearchProperties");
                reportParams.append("dropdownTypes", STATUS_DROPDOWN);
                const [a, p] = await Promise.all([
                    apiService.getApiParamater("COMMISSION_MULTI", `?${agentParams.toString()}`),
                    apiService.getApiParamater("REPORT_MULTI_DROP", `?${reportParams.toString()}`),
                ]);
                setAgentList(a?.data?.dropdowns?.AgentCommissionAgentNames || []);
                setPropertyList(p?.data?.dropdowns?.LRAgreeSearchProperties || []);
                setStatusList(p?.data?.dropdowns?.[STATUS_DROPDOWN] || []);
            } catch (e) {
                console.error("Error fetching dropdown data:", e);
            }
        })();
    }, []);

    // An empty selection is sent as an empty string (meaning "all").
    const search = async () => {
        const id = ++reqId.current;
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        // "All" agent type is omitted so the API receives null
        if (formData.agentType && formData.agentType.value !== "All") params.append("AgentType", formData.agentType.value);
        params.append("Property", formData.property?.label || "");
        params.append("BrokerName", formData.broker?.label || "");
        params.append("Status", formData.status?.label || "");
        try {
            const res = await apiService.getApiParamater("REPORT_AGENT_COMMISSION", `?${params.toString()}`);
            if (id !== reqId.current) return;
            setRows(unwrap(res));
            setSearched(true);
        } catch (e) {
            if (id !== reqId.current) return;
            setRows([]);
            setError(e?.message || "Unable to load the report.");
            toast.error("Unable to load the report.");
        } finally {
            if (id === reqId.current) setLoading(false);
        }
    };

    const reset = () => {
        reqId.current++;
        setFormData(DEFAULT_FILTERS());
        setRows([]);
        setSearched(false);
        setError(null);
        setLoading(false);
    };

    const [shownCount, setShownCount] = useState(0);
    const colDefs = useMemo(() => toColDefs(COLS), []);
    const sortedOpts = (list) => list.slice()
        .sort((a, b) => a.text.localeCompare(b.text, undefined, { numeric: true, sensitivity: "base" }))
        .map((o) => ({ value: o.value, label: o.text }));

    return (
        <div className="p-3">
            <div className="cost-center-section">
                <div className="d-flex mt-3 cursor-pointer slide-header" onClick={() => setFilterTab(!FilterTab)}>
                    <div className="h6 pt-1">Search Parameters</div>
                </div>
                <div className={`slide-content ${FilterTab ? "open" : "closed"}`}>
                    <div className={`border-box2 p-3 ${FilterTab ? "slide-in" : "slide-out"}`}>
                        <div className="form-group grid-contain">
                            <div className="d-flex">
                                <label className="form-label">Property</label>
                                <Select className="p-0 w-100" menuPortalTarget={document.body} isClearable placeholder="All"
                                    styles={selectStyles} value={formData.property}
                                    onChange={(o) => setFormData((p) => ({ ...p, property: o }))}
                                    options={sortedOpts(propertyList)} />
                            </div>
                            <div className="d-flex">
                                <label className="form-label">Status</label>
                                <Select className="p-0 w-100" menuPortalTarget={document.body} isClearable placeholder="All"
                                    styles={selectStyles} value={formData.status}
                                    onChange={(o) => setFormData((p) => ({ ...p, status: o }))}
                                    options={sortedOpts(statusList)} />
                            </div>
                            <div className="d-flex">
                                <label className="form-label">Agent</label>
                                <Select className="p-0 w-100" menuPortalTarget={document.body} isClearable placeholder="All"
                                    styles={selectStyles} value={formData.broker}
                                    onChange={(o) => setFormData((p) => ({ ...p, broker: o }))}
                                    options={sortedOpts(agentList)} />
                            </div>
                            <div className="d-flex">
                                <label className="form-label">Agent Type</label>
                                <Select className="p-0 w-100" menuPortalTarget={document.body}
                                    styles={selectStyles} value={formData.agentType}
                                    onChange={(o) => setFormData((p) => ({ ...p, agentType: o || AGENT_TYPES[0] }))}
                                    options={AGENT_TYPES} />
                            </div>
                            <div className="d-flex gap-1">
                                {loading ? (
                                    <div className="mainbtnGry flex-end gap-2 w-50">
                                        <ClipLoader color="#ffffff" className="mt-1" size={25} />
                                    </div>
                                ) : (
                                    <div onClick={search} className="mainbtnGry flex-end gap-2 w-50">
                                        <FaFileCircleExclamation color="#fff" className="center mt-2 mx-2" />
                                        <div className="center">Apply</div>
                                    </div>
                                )}
                                <div onClick={reset} className="mainbtnRed flex-end gap-2 w-50">
                                    <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                                    <div className="center">Reset</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="border-box2 p-3">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <h5 className="col mainInvHead mb-0">Agent Commission Summary</h5>
                    <ExportButtons rows={rows} cols={COLS} title="Agent Commission Summary" file="Agent_Commission_Summary" />
                </div>

                {loading ? (
                    <div className="text-center p-5"><ClipLoader size={30} /></div>
                ) : error ? (
                    <div className="text-danger p-3">{error}</div>
                ) : !searched ? (
                    <div className="text-muted p-3">Set the filters and click Apply.</div>
                ) : !rows.length ? (
                    <div className="text-muted p-3">No commission records found.</div>
                ) : (
                    <div className="gridshad mt-3">
                        <div className="ag-theme-quartz" style={{ height: 650 }}>
                            <AgGridReact
                                getRowStyle={stripe}
                                rowData={rows}
                                enableFilter={true}
                                floatingFilter={true}
                                columnDefs={colDefs}
                                onModelUpdated={(e) => setShownCount(e.api.getDisplayedRowCount())}
                            />
                        </div>
                        <RowCount shown={shownCount} total={rows.length} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AgentCommissionSummary;
